import { DSTypography, Translate } from '@deltasierra/components';
import { AssignedLocation, ExportPlatforms, ISize, LocationIdHierarchy, PreparedVideoRequestData } from '@deltasierra/shared';
import * as React from 'react';
import { OneWayBinding } from '../../../../common/angularData';
import { withAngularIntegration } from '../../../../common/componentUtils/reactComponentRegistration';
import { useFacebookVideoPublishCallback } from '../../facebook/useFacebookVideoPublishCallback';
import { useInstagramVideoPublishCallback } from '../../instagramDirect/useInstagramVideoPublishCallback';
import { usePrepareVideo, VideoUploadProgress } from '../../instagramDirect/VideoUploadProgress';
import { MultipleLocationVideoPublish } from '../../multipleLocationPublish/MultipleLocationVideoPublish';
import { MvContentBuilderPublishCtrl } from '../../mvContentBuilderPublishCtrl';
import { PublishResult } from '../../publishResult';
import { PublishFinishCallback } from '../../types';
import { FacebookAndInstagramData } from './facebook-and-instagram-data';
import { PublishToFacebookAndInstagramDirectForm } from './PublishToFacebookAndInstagramDirectForm';

export type PublishVideoToFacebookAndInstagramDirectProps = {
    location: AssignedLocation;
    locations: LocationIdHierarchy[];
    onCancel: () => void;
    onFinish: (data: PublishResult[]) => void;
    publishController: MvContentBuilderPublishCtrl;
};

// eslint-disable-next-line max-lines-per-function
function PublishVideoToFacebookAndInstagramDirect({
    ...props
}: PublishVideoToFacebookAndInstagramDirectProps): JSX.Element {
    const { location, locations, onCancel, onFinish, publishController } = props;
    /** Holds overall publish state */
    const [isPublishing, setIsPublishing] = React.useState(false);
    const [isFacebookPublishing, setIsFacebookPublishing] = React.useState(false);
    const [isInstagramPublishing, setIsInstagramPublishing] = React.useState(false);
    /** This is typed as InstagramDirectPublishFormData, but should also be typed for FacebookPublishData. They have the same properties */
    const [publishData, setPublishData] = React.useState<FacebookAndInstagramData | null>(null);
    const [publishResults, setPublishResults] = React.useState<PublishResult[]>([]);
    const [videoData, setVideoData] = React.useState<PreparedVideoRequestData | null>(null);
    const [outputDimensions, setoutputDimensions] = React.useState<ISize>({
        height: publishController.contentBuilder.document.dimensions.height,
        width: publishController.contentBuilder.document.dimensions.width,
    });

    const [prepareVideo, videoProcessingContext] = usePrepareVideo();

    const instagramVideoPublishCallback = useInstagramVideoPublishCallback(publishController, videoData);
    const facebookVideoPublishCallback = useFacebookVideoPublishCallback(publishController, videoData);

    const isUploading = videoProcessingContext.uploadContext.uploadInProgress;

    const onPublish = async (data: FacebookAndInstagramData) => {
        setIsPublishing(true);
        setPublishData(data);

        if (publishController.fileFormatChoice) {
            setoutputDimensions({
                height: publishController.fileFormatChoice.height,
                width: publishController.fileFormatChoice.width,
            });
        }
        const preparedVideoData = await prepareVideo(
            publishController.contentBuilder.document,
            outputDimensions,
            publishController,
        );
        setVideoData(preparedVideoData);
        setIsFacebookPublishing(true);
        setIsInstagramPublishing(true);
    };

    const instagramFinishCallback: PublishFinishCallback = () => {
        const scheduledTime = (publishData && publishData.instagram.scheduledTime) || null;
        setPublishResults(existing =>
            existing.concat(new PublishResult(ExportPlatforms.InstagramDirect, scheduledTime)),
        );
        setIsInstagramPublishing(false);
    };

    const facebookFinishCallback: PublishFinishCallback = () => {
        const scheduledTime = (publishData && publishData.facebook.scheduledTime) || null;
        setPublishResults(existing => existing.concat(new PublishResult(ExportPlatforms.Facebook, scheduledTime)));
        setIsFacebookPublishing(false);
    };

    React.useEffect(() => {
        if (publishResults.length === 2) {
            onFinish(publishResults);
        }
    }, [publishResults, onFinish]);

    return (
        <>
            {!isPublishing && !isUploading && !videoData && (
                <PublishToFacebookAndInstagramDirectForm {...props} publishType="video" onPublish={onPublish} />
            )}
            {isUploading && <VideoUploadProgress uploadContext={videoProcessingContext.uploadContext} />}
            {isPublishing && !isUploading && videoData && publishData && (
                <>
                    <DSTypography component="label" variant="h6">
                        {isInstagramPublishing ? (
                            <Translate
                                keyId="BUILD.PUBLISH.MULTIPLE_PLATFORMS.PUBLISHING_TO_PLATFORM"
                                options={{ platform: 'Instagram' }}
                            />
                        ) : (
                            <Translate
                                keyId="BUILD.PUBLISH.MULTIPLE_PLATFORMS.PUBLISHED_TO_PLATFORM"
                                options={{ platform: 'Instagram' }}
                            />
                        )}
                    </DSTypography>
                    {/** Instagram */}
                    <MultipleLocationVideoPublish
                        commonData={publishData.instagram}
                        contentBuilder={publishController.contentBuilder}
                        locations={locations}
                        originalLocation={location}
                        publishCallback={instagramVideoPublishCallback}
                        publishCtrl={publishController}
                        onCancel={onCancel}
                        onFinish={instagramFinishCallback}
                    />
                    <DSTypography component="label" variant="h6">
                        {isFacebookPublishing ? (
                            <Translate
                                keyId="BUILD.PUBLISH.MULTIPLE_PLATFORMS.PUBLISHING_TO_PLATFORM"
                                options={{ platform: 'Facebook' }}
                            />
                        ) : (
                            <Translate
                                keyId="BUILD.PUBLISH.MULTIPLE_PLATFORMS.PUBLISHED_TO_PLATFORM"
                                options={{ platform: 'Facebook' }}
                            />
                        )}
                    </DSTypography>
                    {/** Facebook */}
                    <MultipleLocationVideoPublish
                        commonData={publishData.facebook}
                        contentBuilder={publishController.contentBuilder}
                        locations={locations}
                        originalLocation={location}
                        publishCallback={facebookVideoPublishCallback}
                        publishCtrl={publishController}
                        onCancel={onCancel}
                        onFinish={facebookFinishCallback}
                    />
                </>
            )}
        </>
    );
}
PublishVideoToFacebookAndInstagramDirect.displayName = 'PublishVideoToFacebookAndInstagramDirect';

export default withAngularIntegration(
    PublishVideoToFacebookAndInstagramDirect,
    'publishVideoToFacebookAndInstagramDirect',
    {
        location: OneWayBinding,
        locations: OneWayBinding,
        onCancel: OneWayBinding,
        onFinish: OneWayBinding,
        publishController: OneWayBinding,
    },
);
